/**
 * Feature E04-F02 — Inbox ACTION typed api-client + Query hooks (T05 web side):
 * link / unlink / create-project / reply.
 *
 * Feature-owned (NOT in the shared queries.ts). Transport uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalises the response,
 * never throws) + `unwrap`. Contracts come from `@eer/shared` (inbox-actions.ts)
 * so the SPA speaks the exact shapes the Fastify route validates.
 *
 * Endpoints (routes/inbox-actions.ts, under /emails):
 *   POST /emails/:id/link            { project_id }  → useLinkEmail()
 *   POST /emails/:id/unlink                          → useUnlinkEmail()
 *   POST /emails/:id/create-project                  → useCreateProjectFromEmail()
 *   POST /emails/:id/reply           { to,subject,body } → useReplyEmail()
 *   POST /inbox/check                {}              → useCheckInbox()
 *
 * Each mutation invalidates the emails domain so the feed/conversation reloads
 * after the action (legacy parity: every action re-ran loadEmails).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  inboxActionSuccessSchema,
  createProjectFromEmailResponseSchema,
  inboxCheckResponseSchema,
  inboxCheckStatusResponseSchema,
  isTerminalInboxCheckStatus,
  type InboxActionSuccess,
  type CreateProjectFromEmailResponse,
  type ReplyEmailBody,
  type InboxCheckRequest,
  type InboxCheckResponse,
  type InboxCheckStatusResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

/** POST /emails/:id/link — link an email to an existing (active) project. */
export async function linkEmail(emailId: number, projectId: number): Promise<InboxActionSuccess> {
  const result = await apiClient.post<unknown>(
    `/emails/${emailId}/link`,
    { project_id: projectId },
    { command: 'link_to_project' },
  );
  return inboxActionSuccessSchema.parse(unwrap(result));
}

/** POST /emails/:id/unlink — detach an email from its project. */
export async function unlinkEmail(emailId: number): Promise<InboxActionSuccess> {
  const result = await apiClient.post<unknown>(`/emails/${emailId}/unlink`, undefined, {
    command: 'unlink_from_project',
  });
  return inboxActionSuccessSchema.parse(unwrap(result));
}

/** POST /emails/:id/create-project — create a project FROM an email (LLM extract). */
export async function createProjectFromEmail(
  emailId: number,
): Promise<CreateProjectFromEmailResponse> {
  const result = await apiClient.post<unknown>(
    `/emails/${emailId}/create-project`,
    {},
    { command: 'create_from_email' },
  );
  return createProjectFromEmailResponseSchema.parse(unwrap(result));
}

/** POST /emails/:id/reply — send a plain-text reply via the system mailbox. */
export async function replyEmail(
  emailId: number,
  body: ReplyEmailBody,
): Promise<InboxActionSuccess> {
  const result = await apiClient.post<unknown>(`/emails/${emailId}/reply`, body, {
    command: 'send_reply',
  });
  return inboxActionSuccessSchema.parse(unwrap(result));
}

/**
 * POST /inbox/check — trigger the "Email Checker" for ALL active mailboxes.
 *
 * Body is intentionally empty: no `userId` → the backend processes every active
 * mailbox (Schedule-trigger parity, `inbox-check.ts`). The legacy SPA
 * (`app.checkMail`) sent `{ userId, email, session_token }`; in v2 identity comes
 * from the session Bearer token, so none of that is needed in the body.
 */
export async function checkInbox(): Promise<InboxCheckResponse> {
  const body: InboxCheckRequest = {};
  const result = await apiClient.post<unknown>('/inbox/check', body, {
    command: 'check_mail',
  });
  return inboxCheckResponseSchema.parse(unwrap(result));
}

/**
 * GET /inbox/check/:jobId/status — the current lifecycle state of a previously
 * enqueued manual check (`waiting|active|completed|failed|unknown`). Polled by
 * {@link useInboxCheckStatus} until terminal (E04-F01-T09).
 */
export async function fetchInboxCheckStatus(jobId: string): Promise<InboxCheckStatusResponse> {
  const result = await apiClient.get<unknown>(`/inbox/check/${jobId}/status`, {
    command: 'check_mail_status',
  });
  return inboxCheckStatusResponseSchema.parse(unwrap(result));
}

/** Invalidate the whole emails domain (feed + details) after an action. */
function useInvalidateEmails(): () => void {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: qk.emails.all });
}

/** Link mutation — reloads the feed so the email shows as Linked. */
export function useLinkEmail(): UseMutationResult<
  InboxActionSuccess,
  ApiResultError,
  { emailId: number; projectId: number }
> {
  const invalidate = useInvalidateEmails();
  return useMutation<InboxActionSuccess, ApiResultError, { emailId: number; projectId: number }>({
    mutationFn: ({ emailId, projectId }) => linkEmail(emailId, projectId),
    onSuccess: invalidate,
  });
}

/** Unlink mutation — reloads the feed so the email drops the Linked badge. */
export function useUnlinkEmail(): UseMutationResult<InboxActionSuccess, ApiResultError, number> {
  const invalidate = useInvalidateEmails();
  return useMutation<InboxActionSuccess, ApiResultError, number>({
    mutationFn: (emailId) => unlinkEmail(emailId),
    onSuccess: invalidate,
  });
}

/** Create-project mutation — returns the new project id (host navigates to it). */
export function useCreateProjectFromEmail(): UseMutationResult<
  CreateProjectFromEmailResponse,
  ApiResultError,
  number
> {
  const invalidate = useInvalidateEmails();
  return useMutation<CreateProjectFromEmailResponse, ApiResultError, number>({
    mutationFn: (emailId) => createProjectFromEmail(emailId),
    onSuccess: invalidate,
  });
}

/** Reply mutation — reloads the conversation so the outbound message appears. */
export function useReplyEmail(): UseMutationResult<
  InboxActionSuccess,
  ApiResultError,
  { emailId: number; body: ReplyEmailBody }
> {
  const invalidate = useInvalidateEmails();
  return useMutation<InboxActionSuccess, ApiResultError, { emailId: number; body: ReplyEmailBody }>(
    {
      mutationFn: ({ emailId, body }) => replyEmail(emailId, body),
      onSuccess: invalidate,
    },
  );
}

/**
 * Delay (ms) before the check-mail success handler refetches the feed.
 *
 * POST /inbox/check does NOT do the work synchronously. In this deployment
 * REDIS_URL is configured, so the route (`api/routes/inbox-check.ts`) always takes
 * the async BullMQ path: it ENQUEUES a job and replies immediately with a static
 * `{ message:'Email check queued' }` ack — BEFORE the emailChecker worker (a
 * sequential per-mailbox OAuth-refresh + IMAP/Graph fetch + classify + save loop,
 * genuinely slow) has fetched or saved anything. There is no job-completion signal
 * anywhere in this codebase (no websocket / SSE / job-id polling), so invalidating
 * at t≈0 would just refetch the SAME list and read as "the check found nothing" —
 * a false negative. We therefore delay the refetch to give the worker realistic
 * time to finish, mirroring the legacy SPA's deliberate `setTimeout(loadEmails,
 * 5000)` buffer (`EER app/public/assets/js/project.js` `checkMail`), which existed
 * for exactly this async-job reason. This is a straight port, not a redesign.
 */
export const CHECK_INBOX_REFRESH_DELAY_MS = 5000;

/** Poll cadence for the check-mail completion indicator (E04-F01-T09). */
export const INBOX_CHECK_POLL_INTERVAL_MS = 2000;

/**
 * Check-mail mutation — triggers the email checker; the host then drives the
 * completion indicator off the returned `jobId`.
 *
 * The success/error TOAST (raised by the host) fires immediately: it reflects the
 * enqueue ack, which IS accurate and instant. The feed refetch is handled ONE of
 * two ways depending on the response:
 *   - `jobId` present (async BullMQ path) → the host polls
 *     {@link useInboxCheckStatus} and invalidates on the TERMINAL state, so the
 *     refetch happens exactly when the worker finished — no blind timer needed.
 *   - `jobId` absent (synchronous no-Redis fallback, or a broker that returned no
 *     id) → the work was already done when the response arrived, so we fall back to
 *     the legacy buffered refetch after {@link CHECK_INBOX_REFRESH_DELAY_MS}.
 *
 * Either way we NEVER refetch synchronously at t≈0 — that would read the SAME list
 * before the worker ran (a false negative; see CHECK_INBOX_REFRESH_DELAY_MS).
 */
export function useCheckInbox(): UseMutationResult<InboxCheckResponse, ApiResultError, void> {
  const invalidate = useInvalidateEmails();
  return useMutation<InboxCheckResponse, ApiResultError, void>({
    mutationFn: () => checkInbox(),
    onSuccess: (res) => {
      // With a jobId the caller polls for completion and invalidates on terminal —
      // do NOT also fire a timer (would double-refetch). Without one, fall back to
      // the legacy buffered refetch for the async worker.
      if (!res.jobId) {
        setTimeout(invalidate, CHECK_INBOX_REFRESH_DELAY_MS);
      }
    },
  });
}

/**
 * Poll a manual check's job status until it reaches a terminal state
 * (`completed` / `failed` / `unknown`). Mirrors the research-run status poll
 * (researchOrchestratorApi.ts): `refetchInterval` returns `false` once the last
 * snapshot is terminal, so polling stops on its own; disabled while `jobId` is null
 * (no check in flight). The host reacts to the terminal state (invalidate the feed
 * + done/failed toast); this hook is a pure data source.
 */
export function useInboxCheckStatus(
  jobId: string | null,
): UseQueryResult<InboxCheckStatusResponse, ApiResultError> {
  return useQuery<InboxCheckStatusResponse, ApiResultError>({
    queryKey: ['inbox', 'check', 'status', jobId ?? ''],
    queryFn: () => fetchInboxCheckStatus(jobId as string),
    enabled: jobId !== null,
    // Don't retain a terminal snapshot between checks — a fresh trigger re-polls.
    gcTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && isTerminalInboxCheckStatus(status)) return false;
      return INBOX_CHECK_POLL_INTERVAL_MS;
    },
  });
}
